// Local reimplementation of the former @wemap/routers/helpers/InstructionManager.
// The new @wemap/routers package only exposes its top-level entry point, so this helper
// (which the legacy package shipped under a /helpers/ subpath) lives in the tool instead.
import { Coordinates } from '@wemap/geo';
import { ItineraryInfoManager, isStepLevelChange } from '@wemap/routers';
import type { Step } from '@wemap/routers';
import { diffAngle, rad2deg, roundFactor } from './angle-math.js';

export default class InstructionManager {

    static useProposals = false;

    static getTurnInfoFromAngle(_angle: number) {

        let direction, directionExtra;

        const directionAngle = rad2deg(diffAngle(_angle, Math.PI));
        const directionAngleAbs = Math.abs(directionAngle);
        if (directionAngleAbs <= 20) {
            direction = 'straight';
        } else {
            direction = directionAngle > 0 ? 'left' : 'right';
            if (directionAngleAbs < 55) {
                directionExtra = 'slight';
            } else if (directionAngleAbs > 120) {
                directionExtra = 'sharp';
            }
        }

        return { direction, directionExtra };
    }

    static getInfoFromStep(step: Step) {

        let type, direction, directionExtra, levelChange;

        if (isStepLevelChange(step)) {
            type = 'level-change';
            levelChange = step.levelDifference;
        } else {
            type = 'turn';
            const turnInfo = InstructionManager.getTurnInfoFromAngle(step.angle);
            direction = turnInfo.direction;
            directionExtra = turnInfo.directionExtra;
        }

        return {
            type,
            direction,
            directionExtra,
            levelChange,
            name: step.name,
            indoor: step.coords.level !== null
        };
    }

    // eslint-disable-next-line max-statements, complexity
    static getInstructionFromStep(step: Step) {

        const { direction, directionExtra } = InstructionManager.getTurnInfoFromAngle(step.angle);
        const isTurn = direction !== 'straight';

        if (step.lastStep) {
            if (isTurn && direction === 'left') {
                return 'Your destination is on your left';
            } else if (isTurn && direction === 'right') {
                return 'Your destination is on your right';
            }
        }

        let suffix = '';
        if (step.type === 'gate') {
            suffix = ` on gate ${step.name}`;
        } else if (step.name) {
            suffix = ` on ${step.name}`;
        }

        if (isStepLevelChange(step)) {
            if (step.direction === 'up') {
                if (step.type === 'stairs') {
                    return 'Go up the stairs';
                }
                if (step.type === 'escalator') {
                    return 'Go up the escalator';
                }
                if (step.type === 'elevator') {
                    return 'Go up the elevator';
                }
                if (step.type === 'moving-walkway') {
                    return 'Go up the moving walkway';
                }
                if (step.type === 'incline-plane') {
                    return 'Go up the incline plane';
                }
                return 'Go up' + suffix;
            }
            if (step.direction === 'down') {
                if (step.type === 'stairs') {
                    return 'Go down the stairs';
                }
                if (step.type === 'escalator') {
                    return 'Go down the escalator';
                }
                if (step.type === 'elevator') {
                    return 'Go down the elevator';
                }
                if (step.type === 'moving-walkway') {
                    return 'Go down the moving walkway';
                }
                if (step.type === 'incline-plane') {
                    return 'Go down the incline plane';
                }
                return 'Go down' + suffix;
            }
        }

        if (step.type === 'subway-entrance') {
            return `Take exit ${step.name}`;
        }

        if (isTurn) {
            if (direction === 'left') {
                if (directionExtra === 'slight') {
                    return 'Turn slightly left' + suffix;
                }
                return 'Turn left' + suffix;
            }
            if (direction === 'right') {
                if (directionExtra === 'slight') {
                    return 'Turn slightly right' + suffix;
                }
                return 'Turn right' + suffix;
            }
        }

        return 'Continue straight';
    }

    static getInstructionFromPosition(itineraryInfoManager: ItineraryInfoManager, position: Coordinates) {
        const itineraryInfo = itineraryInfoManager.getInfo(position);
        if (!itineraryInfo) {
            return null;
        }

        // If distance between itinerary and position is too far,
        // something went wrong. (mapmatching.maxDistance)
        if (this.useProposals && itineraryInfo.projection.distanceFromNearestElement > 15) {
            return 'It seems that we are a little bit lost, please start again the localization process';
        }

        const { nextStep } = itineraryInfo;
        if (!nextStep) {
            return 'You are arrived';
        }

        const distNextStep = position.distanceTo(nextStep.coords);
        const distRounded = roundFactor(distNextStep, 5);

        if (this.useProposals && distNextStep > 10) {
            return `Continue straight for ${distRounded}m`;
        }

        let instruction = InstructionManager.getInstructionFromStep(nextStep);

        const stepWithImportantInfo = itineraryInfoManager.itinerary?.steps.find(step => isStepLevelChange(step)
            && step.number > nextStep.number
            && step.coords.distanceTo(nextStep.coords) < 10
        ) || null;

        if (stepWithImportantInfo && isStepLevelChange(stepWithImportantInfo)) {
            const nextBearing = nextStep.coords.bearingTo(stepWithImportantInfo.coords);
            const { direction } = InstructionManager.getTurnInfoFromAngle(nextBearing - nextStep.previousBearing);
            instruction = direction === 'straight' ? 'Continue straight' : `Turn ${direction}`;
            const { direction: levelDirection, type: levelType } = stepWithImportantInfo;
            instruction += ` and take the ${levelType} going ${levelDirection}`;
        }

        if (distNextStep >= 5) {
            instruction += ` in ${distRounded}m`;
        }

        return instruction;
    }

}
